import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Chip,
  Divider,
  Alert,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FunctionsIcon from '@mui/icons-material/Functions';
import { abyssColors } from '@/theme/colors';
import {
  AssemblyTemplateField,
  AllTemplatesForBuilderQuery,
  ExpressionType,
  TemplateTypeEnum,
  MathOperator,
  LogicEvaluationStrategy,
} from '@/__generated__/graphql';

const BLISTER_FIELDS = [
  { key: 'estimatedHeight', name: 'Estimated Height' },
  { key: 'manualSetHeight', name: 'Manual Set Height' },
  { key: 'blisterHeightIndex', name: 'Blister Height Index' },
  { key: 'maxHeight', name: 'Max Height' },
  { key: 'wallLoss.value', name: 'Wall Loss Value' },
  { key: 'wallLoss.percentage', name: 'Wall Loss Percentage' },
  { key: 'residualThickness.value', name: 'Residual Thickness Value' },
  { key: 'residualThickness.percentage', name: 'Residual Thickness Percentage' },
  { key: 'residualCorrosionAllowance.value', name: 'Residual Corrosion Allowance Value' },
  { key: 'residualCorrosionAllowance.percentage', name: 'Residual Corrosion Allowance Percentage' },
  { key: 'pdrHeight', name: 'PDR Height' },
  { key: 'verifiedMaxHeight', name: 'Verified Max Height' },
  { key: 'verifiedResidualThickness.value', name: 'Verified Residual Thickness Value' },
  { key: 'verifiedResidualThickness.percentage', name: 'Verified Residual Thickness Percentage' },
  {
    key: 'verifiedResidualCorrosionAllowance.value',
    name: 'Verified Residual Corrosion Allowance Value',
  },
  {
    key: 'verifiedResidualCorrosionAllowance.percentage',
    name: 'Verified Residual Corrosion Allowance Percentage',
  },
];

type EquationType = NonNullable<
  AllTemplatesForBuilderQuery['allTemplates'][number]['equations']
>[number];

interface Variable {
  name: string;
  fieldKey: string;
  templateType: TemplateTypeEnum;
  isBlisterField?: boolean | null;
}

interface ExpressionNode {
  id: string;
  type: ExpressionType;
  value?: number | null;
  variable?: string | null;
  operator?: MathOperator | null;
  args?: ExpressionNode[] | null;
}

interface EquationBuilderProps {
  availableFields: AssemblyTemplateField[];
  equations: EquationType[];
  onUpdate: (equations: EquationType[]) => void;
  evaluationStrategy?: LogicEvaluationStrategy;
  onEvaluationStrategyUpdate?: (strategy: LogicEvaluationStrategy) => void;

  poiTemplates?: any[];
}

const OPERATORS = [
  { value: 'ADD', label: 'Add (+)', symbol: '+' },
  { value: 'SUBTRACT', label: 'Subtract (-)', symbol: '-' },
  { value: 'MULTIPLY', label: 'Multiply (×)', symbol: '×' },
  { value: 'DIVIDE', label: 'Divide (÷)', symbol: '÷' },
  { value: 'POWER', label: 'Power (^)', symbol: '^' },
  { value: 'MODULO', label: 'Modulo (%)', symbol: '%' },
];

const FUNCTIONS = [
  { value: 'SQRT', label: 'Square Root', args: 1 },
  { value: 'ABS', label: 'Absolute Value', args: 1 },
  { value: 'ROUND', label: 'Round', args: 1 },
  { value: 'CEIL', label: 'Ceiling', args: 1 },
  { value: 'FLOOR', label: 'Floor', args: 1 },
  { value: 'MIN', label: 'Minimum', args: 2 },
  { value: 'MAX', label: 'Maximum', args: 2 },
  { value: 'AVG', label: 'Average', args: 2 },
  { value: 'SUM', label: 'Sum', args: -1 },
];

export const EquationBuilder = ({
  availableFields,
  equations,
  onUpdate,
  evaluationStrategy = LogicEvaluationStrategy.FirstMatch,
  onEvaluationStrategyUpdate,
  poiTemplates = [],
}: EquationBuilderProps) => {
  const [localEquations, setLocalEquations] = useState<EquationType[]>(equations);
  const [localEvaluationStrategy, setLocalEvaluationStrategy] =
    useState<LogicEvaluationStrategy>(evaluationStrategy);

  // Sync local state when equations prop changes
  useEffect(() => {
    setLocalEquations(equations);
  }, [equations]);

  // Sync evaluation strategy when prop changes
  useEffect(() => {
    setLocalEvaluationStrategy(evaluationStrategy);
  }, [evaluationStrategy]);

  // Get available fields based on template type
  const getFieldsForTemplateType = useMemo(() => {
    return (templateType: TemplateTypeEnum, isBlister?: boolean) => {
      if (templateType === TemplateTypeEnum.PointOfInterest) {
        if (isBlister) {
          // Return hardcoded blister fields
          return BLISTER_FIELDS as any;
        }
        // Get all POI fields from all POI templates
        const allPoiFields: AssemblyTemplateField[] = [];
        poiTemplates?.forEach((template) => {
          if (template.fields) {
            allPoiFields.push(...template.fields);
          }
        });
        // Remove duplicates by key
        const uniqueFields = allPoiFields.reduce((acc, field) => {
          if (!acc.find((f) => f.key === field.key)) {
            acc.push(field);
          }
          return acc;
        }, [] as AssemblyTemplateField[]);
        return uniqueFields;
      }
      return availableFields;
    };
  }, [poiTemplates, availableFields]);

  const createNewVariable = (): Variable => ({
    name: '',
    fieldKey: '',
    templateType: TemplateTypeEnum.Assembly,
  });

  const createNewExpression = (): ExpressionNode => ({
    id: `expr-${Date.now()}-${Math.random()}`,
    type: ExpressionType.Number,
    value: 0,
  });

  const createNewEquation = (): EquationType => ({
    key: `eq_${Date.now()}`,
    name: '',
    expression: createNewExpression(),
    variables: [],
  });

  const handleAddEquation = () => {
    const newEquations = [...localEquations, createNewEquation()];
    setLocalEquations(newEquations);
    onUpdate(newEquations);
  };

  const handleDeleteEquation = (equationKey: string) => {
    const newEquations = localEquations.filter((eq) => eq.key !== equationKey);
    setLocalEquations(newEquations);
    onUpdate(newEquations);
  };

  const handleUpdateEquation = (equationKey: string, field: string, value: any) => {
    const newEquations = localEquations.map((eq) =>
      eq.key === equationKey ? { ...eq, [field]: value } : eq
    );
    setLocalEquations(newEquations);
    onUpdate(newEquations);
  };

  const handleAddVariable = (equationKey: string) => {
    const newEquations = localEquations.map((eq) => {
      if (eq.key === equationKey) {
        return {
          ...eq,
          variables: [...(eq.variables || []), createNewVariable()],
        };
      }
      return eq;
    });
    setLocalEquations(newEquations);
    onUpdate(newEquations);
  };

  const handleUpdateVariable = (
    equationKey: string,
    variableIndex: number,
    field: keyof Variable,

    value: any
  ) => {
    const newEquations = localEquations.map((eq) => {
      if (eq.key === equationKey) {
        const newVariables = [...(eq.variables || [])];
        newVariables[variableIndex] = { ...newVariables[variableIndex], [field]: value };
        return { ...eq, variables: newVariables };
      }
      return eq;
    });
    setLocalEquations(newEquations);
    onUpdate(newEquations);
  };

  const handleDeleteVariable = (equationKey: string, variableIndex: number) => {
    const newEquations = localEquations.map((eq) => {
      if (eq.key === equationKey) {
        const newVariables = (eq.variables || []).filter(
          (_: any, idx: number) => idx !== variableIndex
        );
        return { ...eq, variables: newVariables };
      }
      return eq;
    });
    setLocalEquations(newEquations);
    onUpdate(newEquations);
  };

  const handleUpdateExpression = (
    equationKey: string,
    field: keyof ExpressionNode,

    value: any
  ) => {
    const newEquations = localEquations.map((eq) => {
      if (eq.key === equationKey) {
        const updatedExpression: any = {
          ...eq.expression,
          [field]: value,
        };

        // Initialize args array when switching to OPERATION
        if (field === 'type') {
          if (value === ExpressionType.Operation) {
            updatedExpression.args = updatedExpression.args || [
              createNewExpression(),
              createNewExpression(),
            ];
            updatedExpression.operator = updatedExpression.operator || 'ADD';
          }
        }

        // Update args count when operator/function changes
        if (field === 'operator' && eq.expression?.type === ExpressionType.Operation) {
          const func = FUNCTIONS.find((f) => f.value === value);
          if (func) {
            const currentArgs = updatedExpression.args || [];
            if (func.args === 1) {
              updatedExpression.args = [currentArgs[0] || createNewExpression()];
            } else if (func.args === 2) {
              updatedExpression.args = [
                currentArgs[0] || createNewExpression(),
                currentArgs[1] || createNewExpression(),
              ];
            } else if (func.args === -1) {
              // Variable args - keep current or start with 2
              updatedExpression.args =
                currentArgs.length > 0
                  ? currentArgs
                  : [createNewExpression(), createNewExpression()];
            }
          }
        }

        return {
          ...eq,
          expression: updatedExpression,
        };
      }
      return eq;
    });
    setLocalEquations(newEquations);
    onUpdate(newEquations);
  };

  const handleUpdateExpressionArg = (
    equationKey: string,
    argIndex: number,
    field: keyof ExpressionNode,

    value: any
  ) => {
    const newEquations = localEquations.map((eq) => {
      if (eq.key === equationKey && eq.expression?.args) {
        const updatedArgs = [...eq.expression.args];
        updatedArgs[argIndex] = { ...updatedArgs[argIndex], [field]: value } as any;

        return {
          ...eq,
          expression: { ...eq.expression, args: updatedArgs } as any,
        };
      }
      return eq;
    });
    setLocalEquations(newEquations);
    onUpdate(newEquations);
  };

  const handleAddExpressionArg = (equationKey: string) => {
    const newEquations = localEquations.map((eq) => {
      if (eq.key === equationKey && eq.expression?.args) {
        return {
          ...eq,
          expression: {
            ...eq.expression,
            args: [...eq.expression.args, createNewExpression()],
          } as any,
        };
      }
      return eq;
    });
    setLocalEquations(newEquations);
    onUpdate(newEquations);
  };

  const handleDeleteExpressionArg = (equationKey: string, argIndex: number) => {
    const newEquations = localEquations.map((eq) => {
      if (eq.key === equationKey && eq.expression?.args) {
        const updatedArgs = eq.expression.args.filter((_: any, idx: number) => idx !== argIndex);
        return {
          ...eq,
          expression: { ...eq.expression, args: updatedArgs } as any,
        };
      }
      return eq;
    });
    setLocalEquations(newEquations);
    onUpdate(newEquations);
  };

  const buildExpressionPreview = (expr: any, variables: Variable[]): string => {
    if (!expr) return '';

    switch (expr.type) {
      case ExpressionType.Number:
      case 'NUMBER':
        return expr.value || '0';
      case ExpressionType.Variable:
      case 'VARIABLE': {
        const variable = variables.find((v) => v.name === expr.variable);
        return variable ? `{${variable.name}}` : expr.variable || '';
      }
      case ExpressionType.Operation:
      case 'OPERATION': {
        // Check if it's an operator or function
        const op = OPERATORS.find((o) => o.value === expr.operator);
        if (op) {
          // Binary operator
          const leftArg = expr.args?.[0] ? buildExpressionPreview(expr.args[0], variables) : '';
          const rightArg = expr.args?.[1] ? buildExpressionPreview(expr.args[1], variables) : '';
          return `(${leftArg} ${op.symbol} ${rightArg})`;
        } else {
          // Function
          const func = FUNCTIONS.find((f) => f.value === expr.operator);

          const funcArgs =
            expr.args?.map((arg: any) => buildExpressionPreview(arg, variables)).join(', ') || '';
          return `${func?.label || expr.operator}(${funcArgs})`;
        }
      }
      default:
        return '';
    }
  };

  return (
    <Box>
      <Stack spacing={3}>
        {/* Evaluation Strategy Section */}
        <Stack spacing={2}>
          <Typography variant="subtitle2" fontWeight={600}>
            Evaluation Strategy
          </Typography>
          <FormControl size="small" sx={{ maxWidth: 300 }}>
            <InputLabel>Strategy</InputLabel>
            <Select
              value={localEvaluationStrategy}
              onChange={(e) => {
                const newStrategy = e.target.value as LogicEvaluationStrategy;
                setLocalEvaluationStrategy(newStrategy);
                if (onEvaluationStrategyUpdate) {
                  onEvaluationStrategyUpdate(newStrategy);
                }
              }}
              label="Strategy"
            >
              <MenuItem value={LogicEvaluationStrategy.FirstMatch}>
                First Match - Stop at first matching condition
              </MenuItem>
              <MenuItem value={LogicEvaluationStrategy.AllMatch}>
                All Match - All conditions must match
              </MenuItem>
              <MenuItem value={LogicEvaluationStrategy.AnyMatch}>
                Any Match - At least one condition must match
              </MenuItem>
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">
            {localEvaluationStrategy === LogicEvaluationStrategy.FirstMatch &&
              'Evaluates conditions in order and returns the value of the first matching condition.'}
            {localEvaluationStrategy === LogicEvaluationStrategy.AllMatch &&
              'All conditional mappings must match for the rule to be satisfied.'}
            {localEvaluationStrategy === LogicEvaluationStrategy.AnyMatch &&
              'At least one conditional mapping must match for the rule to be satisfied.'}
          </Typography>
        </Stack>

        <Divider />

        {/* Equations Section */}
        {localEquations.length === 0 ? (
          <Card elevation={0} sx={{ backgroundColor: abyssColors.neutral[50] }}>
            <CardContent>
              <Stack alignItems="center" spacing={2} py={3}>
                <FunctionsIcon sx={{ fontSize: '3rem', color: abyssColors.neutral[400] }} />
                <Typography variant="body2" color="text.secondary">
                  No equations configured
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddEquation}>
                  Add First Equation
                </Button>
              </Stack>
            </CardContent>
          </Card>
        ) : (
          localEquations.map((equation, index) => (
            <Card key={equation.key} elevation={0} variant="outlined">
              <CardContent>
                <Stack spacing={3}>
                  {/* Equation Header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FunctionsIcon sx={{ color: abyssColors.tertiary[500] }} />
                      <Typography variant="subtitle2" fontWeight={600}>
                        Equation {index + 1}
                      </Typography>
                      <Chip
                        label={equation.key}
                        size="small"
                        sx={{
                          fontFamily: 'monospace',
                          backgroundColor: abyssColors.tertiary[50],
                          color: abyssColors.tertiary[700],
                          fontSize: '0.875rem',
                        }}
                      />
                    </Stack>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteEquation(equation.key)}
                      sx={{ color: abyssColors.error[500] }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>

                  <Divider />

                  {/* Basic Info */}
                  <Stack spacing={2}>
                    <TextField
                      size="small"
                      label="Equation Key"
                      value={equation.key}
                      onChange={(e) => handleUpdateEquation(equation.key, 'key', e.target.value)}
                      placeholder="e.g., wall_thickness_calc"
                      helperText="Unique identifier for this equation"
                      fullWidth
                    />

                    <TextField
                      size="small"
                      label="Equation Name"
                      value={equation.name || ''}
                      onChange={(e) => handleUpdateEquation(equation.key, 'name', e.target.value)}
                      placeholder="e.g., Wall Thickness Calculation"
                      helperText="Human-readable name"
                      fullWidth
                    />
                  </Stack>

                  <Divider />

                  {/* Variables Section */}
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" fontWeight={600}>
                        Variables ({equation.variables?.length || 0})
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddVariable(equation.key)}
                      >
                        Add Variable
                      </Button>
                    </Stack>

                    {equation.variables && equation.variables.length > 0 ? (
                      <Stack spacing={1}>
                        {}
                        {equation.variables.map((variable: any, vIndex: number) => (
                          <Card
                            key={vIndex}
                            elevation={0}
                            sx={{
                              backgroundColor: abyssColors.neutral[50],
                              border: `1px solid ${abyssColors.neutral[200]}`,
                            }}
                          >
                            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                flexWrap="wrap"
                              >
                                <TextField
                                  size="small"
                                  label="Variable Name"
                                  value={variable.name}
                                  onChange={(e) =>
                                    handleUpdateVariable(
                                      equation.key,
                                      vIndex,
                                      'name',
                                      e.target.value
                                    )
                                  }
                                  placeholder="e.g., thickness"
                                  sx={{ minWidth: 180 }}
                                />

                                <FormControl size="small" sx={{ minWidth: 160 }}>
                                  <InputLabel>Template Type</InputLabel>
                                  <Select
                                    value={variable.templateType || TemplateTypeEnum.Assembly}
                                    onChange={(e) => {
                                      handleUpdateVariable(
                                        equation.key,
                                        vIndex,
                                        'templateType',
                                        e.target.value
                                      );
                                      // Reset blister field flag when template type changes
                                      handleUpdateVariable(
                                        equation.key,
                                        vIndex,
                                        'isBlisterField',
                                        false
                                      );
                                      // Reset field key when template type changes
                                      handleUpdateVariable(equation.key, vIndex, 'fieldKey', '');
                                    }}
                                    label="Template Type"
                                  >
                                    <MenuItem value={TemplateTypeEnum.Assembly}>Assembly</MenuItem>
                                    <MenuItem value={TemplateTypeEnum.PointOfInterest}>
                                      Point of Interest
                                    </MenuItem>
                                  </Select>
                                </FormControl>

                                {variable.templateType === TemplateTypeEnum.PointOfInterest && (
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={variable.isBlisterField || false}
                                        onChange={(e) => {
                                          handleUpdateVariable(
                                            equation.key,
                                            vIndex,
                                            'isBlisterField',
                                            e.target.checked
                                          );
                                          // Reset field key when checkbox changes
                                          handleUpdateVariable(
                                            equation.key,
                                            vIndex,
                                            'fieldKey',
                                            ''
                                          );
                                        }}
                                        size="small"
                                      />
                                    }
                                    label="Blister Field"
                                    sx={{ minWidth: 140 }}
                                  />
                                )}

                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                  <InputLabel>Field</InputLabel>
                                  <Select
                                    value={variable.fieldKey}
                                    onChange={(e) =>
                                      handleUpdateVariable(
                                        equation.key,
                                        vIndex,
                                        'fieldKey',
                                        e.target.value
                                      )
                                    }
                                    label="Field"
                                  >
                                    {getFieldsForTemplateType(
                                      variable.templateType || TemplateTypeEnum.Assembly,
                                      variable.isBlisterField || false
                                    ).map((field: any) => (
                                      <MenuItem key={field.id || field.key} value={field.key || ''}>
                                        {field.name}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>

                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteVariable(equation.key, vIndex)}
                                  sx={{ color: abyssColors.error[500] }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    ) : (
                      <Alert severity="info">
                        Add variables to reference field values in your equation
                      </Alert>
                    )}
                  </Stack>

                  <Divider />

                  {/* Expression Builder */}
                  <Stack spacing={2}>
                    <Typography variant="body2" fontWeight={600}>
                      Expression
                    </Typography>

                    <Card elevation={0} sx={{ backgroundColor: abyssColors.tertiary[50] }}>
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                              <InputLabel>Type</InputLabel>
                              <Select
                                value={equation.expression?.type || ExpressionType.Number}
                                onChange={(e) =>
                                  handleUpdateExpression(equation.key, 'type', e.target.value)
                                }
                                label="Type"
                              >
                                <MenuItem value={ExpressionType.Number}>Number</MenuItem>
                                <MenuItem value={ExpressionType.Variable}>Variable</MenuItem>
                                <MenuItem value={ExpressionType.Operation}>
                                  Operation (Operator/Function)
                                </MenuItem>
                              </Select>
                            </FormControl>

                            {equation.expression?.type === ExpressionType.Number && (
                              <TextField
                                size="small"
                                label="Value"
                                type="number"
                                value={equation.expression?.value || '0'}
                                onChange={(e) =>
                                  handleUpdateExpression(equation.key, 'value', e.target.value)
                                }
                                sx={{ minWidth: 150 }}
                              />
                            )}

                            {equation.expression?.type === ExpressionType.Variable && (
                              <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Variable</InputLabel>
                                <Select
                                  value={equation.expression?.variable || ''}
                                  onChange={(e) =>
                                    handleUpdateExpression(equation.key, 'variable', e.target.value)
                                  }
                                  label="Variable"
                                >
                                  {}
                                  {equation.variables?.map((v: any, idx: number) => (
                                    <MenuItem key={idx} value={v.name}>
                                      {v.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}

                            {equation.expression?.type === ExpressionType.Operation && (
                              <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel>Operator/Function</InputLabel>
                                <Select
                                  value={equation.expression?.operator || 'ADD'}
                                  onChange={(e) =>
                                    handleUpdateExpression(equation.key, 'operator', e.target.value)
                                  }
                                  label="Operator/Function"
                                >
                                  <MenuItem disabled sx={{ fontWeight: 600 }}>
                                    Operators
                                  </MenuItem>
                                  {OPERATORS.map((op) => (
                                    <MenuItem key={op.value} value={op.value}>
                                      {op.label}
                                    </MenuItem>
                                  ))}
                                  <MenuItem disabled sx={{ fontWeight: 600, mt: 1 }}>
                                    Functions
                                  </MenuItem>
                                  {FUNCTIONS.map((func) => (
                                    <MenuItem key={func.value} value={func.value}>
                                      {func.label}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            )}
                          </Stack>

                          {/* Arguments for OPERATION */}
                          {equation.expression?.type === ExpressionType.Operation &&
                            equation.expression?.args && (
                              <Box>
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{ mb: 1 }}
                                >
                                  <Typography variant="caption" fontWeight={600}>
                                    Arguments ({equation.expression?.args?.length || 0})
                                  </Typography>
                                  {equation.expression?.type === ExpressionType.Operation &&
                                    FUNCTIONS.find((f) => f.value === equation.expression?.operator)
                                      ?.args === -1 && (
                                      <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={() => handleAddExpressionArg(equation.key)}
                                        sx={{ fontSize: '0.75rem' }}
                                      >
                                        Add Argument
                                      </Button>
                                    )}
                                </Stack>
                                <Stack spacing={1}>
                                  {}
                                  {equation.expression?.args?.map((arg: any, argIdx: number) => (
                                    <Card
                                      key={argIdx}
                                      elevation={0}
                                      sx={{
                                        backgroundColor: abyssColors.neutral[50],
                                        border: `1px solid ${abyssColors.neutral[200]}`,
                                      }}
                                    >
                                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        <Stack spacing={1}>
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            justifyContent="space-between"
                                          >
                                            <Typography
                                              variant="caption"
                                              fontWeight={600}
                                              color="text.secondary"
                                            >
                                              Arg {argIdx + 1}
                                            </Typography>
                                            {equation.expression?.type ===
                                              ExpressionType.Operation &&
                                              FUNCTIONS.find(
                                                (f) => f.value === equation.expression?.operator
                                              )?.args === -1 &&
                                              (equation.expression?.args?.length || 0) > 1 && (
                                                <IconButton
                                                  size="small"
                                                  onClick={() =>
                                                    handleDeleteExpressionArg(equation.key, argIdx)
                                                  }
                                                  sx={{ color: abyssColors.error[500] }}
                                                >
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              )}
                                          </Stack>
                                          <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                            flexWrap="wrap"
                                          >
                                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                              <InputLabel>Type</InputLabel>
                                              <Select
                                                value={arg.type || 'NUMBER'}
                                                onChange={(e) =>
                                                  handleUpdateExpressionArg(
                                                    equation.key,
                                                    argIdx,
                                                    'type',
                                                    e.target.value
                                                  )
                                                }
                                                label="Type"
                                              >
                                                <MenuItem value="NUMBER">Number</MenuItem>
                                                <MenuItem value="VARIABLE">Variable</MenuItem>
                                              </Select>
                                            </FormControl>

                                            {arg.type === 'NUMBER' && (
                                              <TextField
                                                size="small"
                                                label="Value"
                                                type="number"
                                                value={arg.value || '0'}
                                                onChange={(e) =>
                                                  handleUpdateExpressionArg(
                                                    equation.key,
                                                    argIdx,
                                                    'value',
                                                    e.target.value
                                                  )
                                                }
                                                sx={{ minWidth: 120 }}
                                              />
                                            )}

                                            {arg.type === 'VARIABLE' && (
                                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                                <InputLabel>Variable</InputLabel>
                                                <Select
                                                  value={arg.variable || ''}
                                                  onChange={(e) =>
                                                    handleUpdateExpressionArg(
                                                      equation.key,
                                                      argIdx,
                                                      'variable',
                                                      e.target.value
                                                    )
                                                  }
                                                  label="Variable"
                                                >
                                                  {}
                                                  {equation.variables?.map(
                                                    (v: any, idx: number) => (
                                                      <MenuItem key={idx} value={v.name}>
                                                        {v.name}
                                                      </MenuItem>
                                                    )
                                                  )}
                                                </Select>
                                              </FormControl>
                                            )}
                                          </Stack>
                                        </Stack>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </Stack>
                              </Box>
                            )}

                          {/* Expression Preview */}
                          <Alert severity="info" icon={<FunctionsIcon />}>
                            <Typography variant="caption" fontWeight={600}>
                              Preview:
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', mt: 0.5 }}>
                              {buildExpressionPreview(
                                equation.expression,
                                equation.variables || []
                              ) || 'Configure expression above'}
                            </Typography>
                          </Alert>

                          <Alert severity="warning">
                            <Typography variant="caption">
                              <strong>Note:</strong> Complex nested expressions with multiple
                              operators and functions should be configured via the backend API for
                              proper tree structure.
                            </Typography>
                          </Alert>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))
        )}

        {/* Add Equation Button */}
        {localEquations.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddEquation}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add Another Equation
          </Button>
        )}
      </Stack>
    </Box>
  );
};
